/**
 * UpgradeOverlay — 家庭版升级商用版浮层 (W29, 2026-08)。
 * 触发: family 用户点 Work 入口 / 我的助理页「查看商用版能力」(仅意图时刻, 不主动弹)。
 * 三层渐进披露: 5 强项对比 → 「查看全部功能」展开场景示例 → 立即升级 (business_upgrade checkout)。
 * 门控: 显式用户点击永远放行; 节流仅保留给未来的自动触发场景(当前无)。dismiss 计数只做数据分析。
 * 埋点 (cost=0, usage_log): family_work_click / upgrade_overlay_open /
 *   upgrade_overlay_dismiss / upgrade_checkout_start; 升级完成由 webhook 记 upgrade_complete。
 * 收费透明度: 金额动态读取 admin 定价; 页面明示, 全链路不出现支付服务商字样。
 */
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../api';
import { useT } from '../i18n';
import { useNotify } from '../NotificationContext';
import { fmtCents } from '../utils/money';
import { X, Check, ChevronDown, ChevronUp, Loader2, Sparkles, ArrowUpCircle, Briefcase } from 'lucide-react';

const UpgradeContext = createContext({ openUpgradeOverlay: () => {} });
export function useUpgrade() { return useContext(UpgradeContext); }

const K_SESSION_DISMISS = 'oz_upgrade_session_dismiss';
const K_WEEK = 'oz_upgrade_week';
const K_MONTH = 'oz_upgrade_month';

function weekKey() {
  const d = new Date();
  const day = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const w = new Date(day);
  w.setUTCDate(day.getUTCDate() - day.getUTCDay());
  return w.toISOString().slice(0, 10);
}
function monthKey() { return new Date().toISOString().slice(0, 7); }
function readJSON(k, fb) {
  try { const v = JSON.parse(localStorage.getItem(k)); return v && typeof v === 'object' ? v : fb; } catch (_) { return fb; }
}
function writeJSON(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (_) {} }

const STRENGTH_KEYS = ['s1', 's2', 's3', 's4', 's5'];

export function UpgradeProvider({ children }) {
  const t = useT();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [entry, setEntry] = useState('mode_switch');

  useEffect(() => {
    if (open) api.get('/wallet/plan-pricing').then(r => setPricing(r.data)).catch(() => {});
  }, [open]);

  const emit = useCallback((action, source) => {
    api.post('/usage/events', { action, source }).catch(() => {});
  }, []);

  const bumpOpen = useCallback(() => {
    const wk = readJSON(K_WEEK, { key: '', count: 0 });
    writeJSON(K_WEEK, { key: weekKey(), count: wk.key === weekKey() ? wk.count + 1 : 1 });
    const mo = readJSON(K_MONTH, { key: '', count: 0, dismiss: 0 });
    writeJSON(K_MONTH, {
      key: monthKey(),
      count: mo.key === monthKey() ? mo.count + 1 : 1,
      dismiss: mo.key === monthKey() ? mo.dismiss : 0,
    });
  }, []);

  const bumpDismiss = useCallback(() => {
    try { sessionStorage.setItem(K_SESSION_DISMISS, '1'); } catch (_) {}
    const mo = readJSON(K_MONTH, { key: '', count: 0, dismiss: 0 });
    writeJSON(K_MONTH, {
      key: monthKey(),
      count: mo.key === monthKey() ? mo.count : 0,
      dismiss: mo.key === monthKey() ? mo.dismiss + 1 : 1,
    });
  }, []);

  const openUpgradeOverlay = useCallback((source = 'mode_switch') => {
    emit('family_work_click', source);
    bumpOpen();
    setEntry(source);
    setExpanded(false);
    setBusy(false);
    setOpen(true);
    emit('upgrade_overlay_open', source);
  }, [bumpOpen, emit]);

  const close = useCallback(() => setOpen(false), []);

  const dismiss = useCallback(() => {
    bumpDismiss();
    emit('upgrade_overlay_dismiss', entry);
    setOpen(false);
  }, [bumpDismiss, emit, entry]);

  const upgrade = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    emit('upgrade_checkout_start', entry);
    try {
      const r = await api.post('/wallet/plan-checkout', { plan: 'business_upgrade' });
      if (r.data?.checkout_url) { window.location.href = r.data.checkout_url; return; }
      setBusy(false);
    } catch (e) {
      setBusy(false);
      notify.notifyWarn(e?.response?.data?.error || t('upgrade_overlay.checkout_failed'));
    }
  }, [busy, emit, entry, notify, t]);

  const price = pricing?.plans?.business_upgrade?.price_cents != null
    ? fmtCents(pricing.plans.business_upgrade.price_cents)
    : '$2,500';
  const extFee = pricing?.external_key_price_cents != null
    ? fmtCents(pricing.external_key_price_cents)
    : '$199';

  return (
    <UpgradeContext.Provider value={{ openUpgradeOverlay }}>
      {children}
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-4"
          style={{ backgroundColor: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}>
          <div className="absolute inset-0" onClick={close} aria-hidden="true" />
          <div className="relative w-full max-w-md rounded-2xl p-5 flex flex-col gap-4"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-card)' }}
            role="dialog" aria-modal="true" aria-label={t('upgrade_overlay.title')}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0" style={{ background: 'var(--bg-active)' }}>
                  <Briefcase size={18} style={{ color: 'var(--text-brand)' }} />
                </span>
                <div>
                  <h2 className="font-bold" style={{ color: 'var(--text-primary)' }}>{t('upgrade_overlay.title')}</h2>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('upgrade_overlay.subtitle')}</p>
                </div>
              </div>
              <button onClick={close} className="p-1.5 rounded-lg shrink-0" style={{ color: 'var(--text-secondary)' }}
                aria-label={t('common.close')}>
                <X size={18} />
              </button>
            </div>

            <div className="flex items-end justify-between gap-2">
              <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{price}</div>
              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold"
                style={{ background: 'rgba(59,130,246,0.10)', color: 'var(--text-brand)', border: '1px solid rgba(59,130,246,0.25)' }}>
                {t('upgrade_overlay.one_time')}
              </span>
            </div>

            <ul className="flex flex-col gap-2.5">
              {STRENGTH_KEYS.map(k => (
                <li key={k} className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-5 h-5 rounded-md inline-flex items-center justify-center shrink-0" style={{ background: 'var(--bg-active)' }}>
                    <Sparkles size={12} style={{ color: 'var(--text-brand)' }} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {t(`upgrade_overlay.${k}_title`)}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {t(`upgrade_overlay.${k}_desc`)}
                      {k === 's5' && (
                        <span className="block mt-0.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                          {t('upgrade_overlay.s5_fee', { price: extFee })}
                        </span>
                      )}
                    </p>
                    {expanded && (
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                        {t(`upgrade_overlay.${k}_ex`)}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <p className="text-xs inline-flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
              <Check size={12} style={{ color: 'var(--text-brand)' }} /> {t('upgrade_overlay.bonus')}
            </p>

            <button onClick={() => setExpanded(v => !v)}
              className="self-start inline-flex items-center gap-1 text-xs font-semibold"
              style={{ color: 'var(--text-brand)' }}>
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              {t(expanded ? 'upgrade_overlay.view_less' : 'upgrade_overlay.view_all')}
            </button>

            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('upgrade_overlay.data_kept')}</p>

            <div className="flex flex-col gap-2">
              <button onClick={upgrade} disabled={busy} className="btn-primary w-full"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                {busy ? <Loader2 size={15} className="spin" /> : <ArrowUpCircle size={15} />}
                {t('upgrade_overlay.cta_upgrade')}
              </button>
              <button onClick={dismiss} className="btn-secondary w-full"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                {t('upgrade_overlay.cta_later')}
              </button>
            </div>
          </div>
        </div>
      )}
    </UpgradeContext.Provider>
  );
}
